"use client";

import React from "react";
import { Stethoscope, ClipboardList, Sparkles, Video, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const features = [{
  id: "01",
  title: "Same-Day or Next-Day Access",
  description: "Skip the wait times. Connect with your doctor when you need it most through same-day or next-day appointments tailored to your schedule.",
  icon: Stethoscope
}, {
  id: "02",
  title: "Direct Physician Contact",
  description: "No middleman. Message or call your doctor directly for quick answers, prescription refills, and continuous coordination of your care.",
  icon: Video
}, {
  id: "03",
  title: "Extended Consultations",
  description: "Never feel rushed. Our 30-60 minute visits ensure every health concern is addressed with the attention and depth you deserve.",
  icon: ClipboardList
}, {
  id: "04",
  title: "Transparent Pricing",
  description: "Simple monthly memberships with no hidden fees, copays, or insurance hurdles. Just clear, affordable healthcare for you and your family.",
  icon: Sparkles
}];
function FeatureItem({
  feature,
  index,
  "data-index": dataIndex
}: {
  feature: typeof features[0];
  index: number;
  "data-index"?: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const isEven = index % 2 === 0;
  return <div data-section-id="90304" ref={ref} data-index={dataIndex} className={cn("flex w-full transition-all duration-1000 ease-out", isEven ? "md:justify-start" : "md:justify-end", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
      <div className="relative w-full md:max-w-lg lg:max-w-xl">
        {}
        <div className={cn("absolute top-1/2 -translate-y-1/2 text-[12rem] md:text-[18rem] lg:text-[22rem] font-serif font-black text-primary/5 z-0 select-none pointer-events-none transition-transform duration-1000", "left-1/2 -translate-x-1/2 md:translate-x-0", isEven ? "md:left-auto md:-right-24 lg:-right-40" : "md:right-auto md:-left-24 lg:-left-40", isVisible ? "scale-100" : "scale-90")}>
          {feature.id}
        </div>

        {}
        <div className="relative z-10 flex flex-col sm:flex-row gap-6 items-start bg-card/40 md:bg-transparent backdrop-blur-md md:backdrop-blur-none p-6 md:p-0 rounded-3xl border border-border/50 md:border-none hover:border-primary/60 transition-all duration-400 ease-out">
          <div className="size-16 rounded-2xl bg-ring/10 flex items-center justify-center text-ring shrink-0 shadow-sm border border-ring/20">
            <feature.icon className="size-8" strokeWidth={1.5} />
          </div>
          <div className="pt-2">
            <h3 className="font-sans text-2xl md:text-3xl font-bold text-foreground mb-3 tracking-tight">
              {feature.title}
            </h3>
            <p className="font-serif text-base md:text-lg text-foreground/70 leading-relaxed font-light">
              {feature.description}
            </p>
          </div>
        </div>
      </div>
    </div>;
}
export default function WhyChooseRestore() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.5
  });
  return <section id="the-crescent-difference" className="py-32 overflow-hidden dark text-foreground !bg-[hsl(223,23%,43%)]">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {}
        <div ref={headerRef} className={cn("text-center max-w-3xl mx-auto mb-24 md:mb-32 transition-all duration-1000 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-foreground mb-6 tracking-tight">
            The Crescent Difference
          </h2>
          <p className="font-sans text-lg text-muted-foreground tracking-wide">
            Experience a new standard of family medicine where clinical excellence meets boutique accessibility.
          </p>
        </div>

        {}
        <div className="flex flex-col gap-24 md:gap-40">
          {features.map((feature, index) => <FeatureItem key={feature.id} feature={feature} index={index} data-index={index} />)}
        </div>

        {}
        <div className="mt-32 text-center">
          <Button asChild size="lg" className="rounded-full px-8 py-6 text-lg bg-primary text-primary-foreground shadow-lg shadow-primary/20 transition-all duration-400 hover:scale-105 hover:bg-primary/90">
            <Link to="/ContactPage">Start Membership<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-arrow-right ml-2 size-5" aria-hidden="true" data-ast-id="f:/src/pages/Home/sections/WhyChooseRestore.tsx|sig:28i0wg" data-ast-start="4671" data-ast-end="4709"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg></Link>
          </Button>
        </div>
      </div>
    </section>;
}